import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Switch,
} from '@mui/material';
import { useHistory, useLocation } from 'react-router-dom';
import ArrowBackIos from '@mui/icons-material/ArrowBackIos';
import { useSnackbar } from 'notistack';
import { AxiosError } from 'axios';
import Page from '../../../components/Layout/Page';
import IndicatorsTable from '../components/table';
import { useYear } from '../../../hooks/useYear';
import { getEnrollmentsBySchoolLocation, getEnrollmentsByProgramLocation } from '../../../requests/api/dashboard';
import { DEFAULT_API_ERROR_MESSAGE } from '../../../utils/errors';
import useStyles from './styles';

interface EnrollmentData {
  columns: Array<{ id: string; label: string }>;
  rows: Array<{
    id: string;
    label: string;
    values: Record<string, { amount: number; variation: number }>;
    rightValue?: number;
  }>;
}

const AcademicEnrollments: React.FC = () => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const { selectedYear } = useYear();
  const { enqueueSnackbar } = useSnackbar();

  // Initialize state from URL to avoid double renders
  const initialViewType: 'students' | 'programs' = location.pathname.includes('/programas')
    ? 'programs'
    : 'students';
  const [viewType, setViewType] = useState<'students' | 'programs'>(initialViewType);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<EnrollmentData | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = viewType === 'programs'
          ? await getEnrollmentsByProgramLocation(selectedYear)
          : await getEnrollmentsBySchoolLocation(selectedYear);
        setData(response);
      } catch (err) {
        const e = err as AxiosError;
        enqueueSnackbar(e.response?.data?.message || DEFAULT_API_ERROR_MESSAGE, {
          variant: 'error',
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedYear, viewType, enqueueSnackbar]);

  const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newViewType = event.target.checked ? 'programs' : 'students';
    setViewType(newViewType);
    // Update URL without reloading
    const newPath = newViewType === 'programs'
      ? '/dashboard/academico/inscripciones/programas'
      : '/dashboard/academico/inscripciones/alumnos';
    history.push(newPath);
  };

  const handleGoBack = () => {
    history.push('/dashboard/academico');
  };

  const title = viewType === 'programs' ? 'Programas inscritos' : 'Alumnos inscritos';

  if (loading) {
    return (
      <Page title={title}>
        <Box className={classes.headerRow}>
          <Box className={classes.backButtonContainer}>
            <IconButton
              onClick={handleGoBack}
              className={classes.backButton}
              size="small"
            >
              <ArrowBackIos className={classes.backIcon} />
            </IconButton>
            <Typography className={classes.pageTitle}>
              {title}
            </Typography>
          </Box>
          <Box className={classes.switchContainer}>
            <Typography>Alumnos</Typography>
            <Switch
              checked={viewType === 'programs'}
              onChange={handleSwitchChange}
              color="primary"
            />
            <Typography>Programas</Typography>
          </Box>
        </Box>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <CircularProgress />
        </Box>
      </Page>
    );
  }

  return (
    <Page title={title}>
      <Box className={classes.headerRow}>
        <Box className={classes.backButtonContainer}>
          <IconButton
            onClick={handleGoBack}
            className={classes.backButton}
            size="small"
          >
            <ArrowBackIos className={classes.backIcon} />
          </IconButton>
          <Typography className={classes.pageTitle}>
            {title}
          </Typography>
        </Box>
        <Box className={classes.switchContainer}>
          <Typography>Alumnos</Typography>
          <Switch
            checked={viewType === 'programs'}
            onChange={handleSwitchChange}
            color="primary"
          />
          <Typography>Programas</Typography>
        </Box>
      </Box>

      <Box className={classes.enrollmentsContainer}>
        {data && (
          <IndicatorsTable
            columns={data.columns}
            rows={data.rows}
            currencySymbol=""
          />
        )}
      </Box>
    </Page>
  );
};

export default AcademicEnrollments;
